import { RefObject, useCallback, useEffect, useRef, useState } from "react";

interface ContainerRect {
  left: number;
  top: number;
  width: number;
  right: number;
}

/**
 * Hook that tracks the bounding rect of a container element using requestAnimationFrame polling.
 * Useful for positioning fixed elements relative to a container.
 *
 * Uses continuous polling via requestAnimationFrame to detect both size and position changes,
 * which covers scenarios that ResizeObserver misses (like position-only changes from sibling
 * elements resizing, sidebar toggles, etc.).
 *
 * Performance is optimized by only triggering state updates when rect values actually change.
 *
 * @param externalRef - Optional external ref to use instead of creating an internal one
 * @returns Object containing:
 *   - containerRef: Ref to attach to the container element (either the external ref or internal one)
 *   - containerRect: The container's position (left, top, width, right) or null if not yet measured
 *   - updateRect: Function to manually trigger a rect update
 *
 * @example
 * ```tsx
 * // Using internal ref
 * const { containerRef, containerRect } = useContainerRect();
 *
 * // Using external ref
 * const myRef = useRef<HTMLDivElement>(null);
 * const { containerRect } = useContainerRect(myRef);
 *
 * return (
 *   <div ref={containerRef}>
 *     <div
 *       style={{
 *         position: isSticky ? "fixed" : "relative",
 *         left: isSticky ? containerRect?.left : "auto",
 *         width: isSticky ? containerRect?.width : "auto",
 *       }}
 *     >
 *       Content
 *     </div>
 *   </div>
 * );
 * ```
 */
export const useContainerRect = (externalRef?: RefObject<HTMLDivElement>) => {
  const internalRef = useRef<HTMLDivElement>(null);
  const containerRef = externalRef ?? internalRef;
  const lastRectRef = useRef<ContainerRect | null>(null);
  const rafIdRef = useRef<number | null>(null);

  const [containerRect, setContainerRect] = useState<ContainerRect | null>(
    null,
  );

  const updateRect = useCallback(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const newRect = {
        left: rect.left,
        top: rect.top,
        width: rect.width,
        right: rect.right,
      };

      // Only update state if values actually changed
      const lastRect = lastRectRef.current;
      if (
        !lastRect ||
        lastRect.left !== newRect.left ||
        lastRect.top !== newRect.top ||
        lastRect.width !== newRect.width ||
        lastRect.right !== newRect.right
      ) {
        lastRectRef.current = newRect;
        setContainerRect(newRect);
      }
    }
  }, [containerRef]);

  useEffect(() => {
    const pollPosition = () => {
      updateRect();
      rafIdRef.current = requestAnimationFrame(pollPosition);
    };
    rafIdRef.current = requestAnimationFrame(pollPosition);

    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, [containerRef, updateRect]);

  return { containerRef, containerRect, updateRect };
};
